import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Mail, Heart, ExternalLink, Cookie, HelpCircle, PawPrint, Instagram, Facebook, ArrowRight, BookOpen, ChevronDown, Newspaper, LinkIcon } from "lucide-react";
import { FAQModalLazy } from "@/components/FAQModalLazy";
import { Brand, BRAND_NAME } from "./Brand";
import { articles } from "@/content/articles";

export const Footer = () => {
  const { isAdmin } = useAuth();
  const { resetConsent } = useCookieConsent();
  const [openSection, setOpenSection] = useState<"articles" | "resources" | null>(null);

  const toggleSection = (s: "articles" | "resources") =>
    setOpenSection((curr) => (curr === s ? null : s));

  const linkBase =
    "group block text-[11px] sm:text-xs text-muted-foreground hover:text-foreground transition-colors leading-snug";

  const resources = [
    { href: "https://napaluchu.waw.pl/edukacja/co-zrobic-jak-znajdziesz-zwierze/", label: "Znalazłeś zwierzę?" },
    { href: "https://fundacjazwierz.org/jak-zglosic-znecanie-sie-nad-zwierzakiem/", label: "Zgłoś znęcanie" },
    { href: "https://www.safe-animal.eu/pl/gdzie-zglosic-zaginiecie-psa", label: "Zaginiony pies/kot" },
    { href: "https://pupilmed.pl/blog/pierwsza-pomoc-u-psa", label: "Pierwsza pomoc" },
  ];

  const socialIcon =
    "w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-background/70 backdrop-blur border border-border/60 hover:border-primary/40 hover:bg-primary/5 flex items-center justify-center transition-all hover:-translate-y-0.5 shadow-sm flex-shrink-0";

  return (
    <footer className="relative mt-8 sm:mt-12 overflow-hidden">
      {/* Soft gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.04] via-background to-background pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-primary/[0.05] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 py-6 sm:py-9 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-12 gap-4 sm:gap-x-8 sm:gap-y-8 lg:gap-10 items-start text-center sm:text-left">
          {/* Brand */}
          <div className="order-1 sm:col-span-3 lg:col-span-4 space-y-3 sm:space-y-4 flex flex-col items-center sm:items-start">
            <Brand showIcon size="md" />
            <p className="hidden sm:block text-[11px] sm:text-xs text-muted-foreground leading-relaxed max-w-sm">
              Pomagamy łączyć ludzi ze schroniskami, fundacjami i organizacjami pomagającymi zwierzętom w całej Polsce.
            </p>

            <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center sm:justify-start sm:pt-1">
              <FAQModalLazy>
                <button className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors">
                  <HelpCircle className="w-3.5 h-3.5" />
                  FAQ
                </button>
              </FAQModalLazy>
              <Link
                to="/schroniska"
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors"
              >
                <PawPrint className="w-3.5 h-3.5" />
                Lista schronisk
              </Link>
              {/* Force brochure to a new line on mobile */}
              <div className="basis-full h-0 sm:hidden" aria-hidden />
              <Link
                to="/dla-schronisk"
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Broszura informacyjna
              </Link>
              <Link
                to="/o-projekcie"
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors"
              >
                <Heart className="w-3.5 h-3.5" />
                O projekcie
              </Link>
            </div>

          </div>


          {/* Articles - order: above contact on mobile */}
          <div className="order-2 sm:order-3 lg:col-span-3 min-w-0">
            {/* Mobile: animated collapsible */}
            <div className="sm:hidden border-t border-border/60">
              <button
                type="button"
                onClick={() => toggleSection("articles")}
                aria-expanded={openSection === "articles"}
                className="w-full flex items-center justify-between py-2 list-none group/btn"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-primary/10 group-hover/btn:bg-primary/15 flex items-center justify-center transition-colors">
                    <Newspaper className="w-3.5 h-3.5 text-primary" />
                  </span>
                  <span className="font-semibold text-[12px] text-foreground/90">
                    Artykuły
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-primary transition-transform duration-300 ${openSection === "articles" ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${openSection === "articles" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <ul className="overflow-hidden space-y-1.5 text-left pl-9 pr-1 [&>li]:transition-all">
                  {articles.slice(0, 4).map((a) => (
                    <li key={a.slug} className="min-w-0">
                      <Link to={`/artykuly/${a.slug}`} className={linkBase}>
                        <span className="line-clamp-2">{a.title}</span>
                      </Link>
                    </li>
                  ))}
                  <li className="pt-0.5 pb-2">
                    <Link
                      to="/artykuly"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                    >
                      Wszystkie artykuły
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>


            {/* Desktop: full list */}
            <div className="hidden sm:block space-y-3">
              <h3 className="font-semibold text-[11px] sm:text-xs uppercase tracking-wider text-foreground/80">
                Artykuły
              </h3>
              <ul className="space-y-2">
                {articles.slice(0, 4).map((a) => (
                  <li key={a.slug} className="min-w-0">
                    <Link to={`/artykuly/${a.slug}`} className={linkBase}>
                      <span className="line-clamp-2">{a.title}</span>
                    </Link>
                  </li>
                ))}
                <li className="pt-1">
                  <Link
                    to="/artykuly"
                    className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-primary hover:gap-1.5 transition-all"
                  >
                    Wszystkie artykuły
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Resources - order: above contact on mobile */}
          <div className="order-3 sm:order-4 lg:col-span-2 min-w-0">
            {/* Mobile: animated collapsible */}
            <div className="sm:hidden border-t border-border/60">
              <button
                type="button"
                onClick={() => toggleSection("resources")}
                aria-expanded={openSection === "resources"}
                className="w-full flex items-center justify-between py-2 list-none group/btn"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-primary/10 group-hover/btn:bg-primary/15 flex items-center justify-center transition-colors">
                    <LinkIcon className="w-3.5 h-3.5 text-primary" />
                  </span>
                  <span className="font-semibold text-[12px] text-foreground/90">
                    Przydatne linki
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-primary transition-transform duration-300 ${openSection === "resources" ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${openSection === "resources" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <ul className="overflow-hidden space-y-1.5 text-left pl-9 pr-1 pb-2">
                  {resources.map((link) => (
                    <li key={link.href} className="min-w-0">
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${linkBase} inline-flex items-center gap-1.5 max-w-full`}
                      >
                        <span className="truncate">{link.label}</span>
                        <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>


            {/* Desktop: full list */}
            <div className="hidden sm:block space-y-3">
              <h3 className="font-semibold text-[11px] sm:text-xs uppercase tracking-wider text-foreground/80">
                Przydatne linki
              </h3>
              <ul className="space-y-2">
                {resources.map((link) => (
                  <li key={link.href} className="min-w-0">
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkBase} inline-flex items-center gap-1.5 max-w-full`}
                    >
                      <span className="truncate">{link.label}</span>
                      <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact - below articles/resources on mobile */}
          <div className="order-4 sm:order-2 lg:col-span-3 min-w-0 flex flex-col items-center sm:items-start border-t sm:border-t-0 border-border/60 pt-3 sm:pt-0 space-y-2.5 sm:space-y-3">
            <h3 className="font-semibold text-[11px] sm:text-xs uppercase tracking-wider text-foreground/80">
              Kontakt
            </h3>
            {/* Mobile: mail + social icons inline */}
            <div className="flex sm:hidden items-center gap-2 max-w-full">
              <a
                href="mailto:kontakt@gdzieschronisko.pl"
                className="inline-flex items-center gap-2 text-[11px] text-foreground/80 hover:text-primary transition-colors group min-w-0"
              >
                <span className="w-7 h-7 rounded-full bg-primary/10 group-hover:bg-primary/15 flex items-center justify-center transition-colors flex-shrink-0">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                </span>
                <span className="truncate">kontakt@gdzieschronisko.pl</span>
              </a>
            </div>
            <div className="flex sm:hidden items-center gap-2">
              <a href="https://www.instagram.com/gdzieschronisko.pl" target="_blank" rel="noopener noreferrer" className={socialIcon} aria-label="Instagram">
                <Instagram className="w-4 h-4 text-primary" />
              </a>
              <a href="https://www.facebook.com/gdzieschronisko" target="_blank" rel="noopener noreferrer" className={socialIcon} aria-label="Facebook">
                <Facebook className="w-4 h-4 text-primary" />
              </a>
            </div>

            {/* Desktop: mail + tagline */}
            <a
              href="mailto:kontakt@gdzieschronisko.pl"
              className="hidden sm:inline-flex items-center gap-2 text-xs text-foreground/80 hover:text-primary transition-colors group max-w-full"
            >
              <span className="w-7 h-7 rounded-full bg-primary/10 group-hover:bg-primary/15 flex items-center justify-center transition-colors flex-shrink-0">
                <Mail className="w-3.5 h-3.5 text-primary" />
              </span>
              <span className="truncate">kontakt@gdzieschronisko.pl</span>
            </a>
            <p className="hidden sm:block text-xs text-muted-foreground leading-relaxed pt-1">
              {BRAND_NAME} to oddolna, niezależna inicjatywa. Obecność jest darmowa.
            </p>
            {/* Desktop: social icons under tagline */}
            <div className="hidden sm:flex items-center gap-2 pt-1">
              <a href="https://www.instagram.com/gdzieschronisko.pl" target="_blank" rel="noopener noreferrer" className={socialIcon} aria-label="Instagram">
                <Instagram className="w-4 h-4 text-primary" />
              </a>
              <a href="https://www.facebook.com/gdzieschronisko" target="_blank" rel="noopener noreferrer" className={socialIcon} aria-label="Facebook">
                <Facebook className="w-4 h-4 text-primary" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-4 sm:mt-8 pt-3 sm:pt-5 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 text-[11px] sm:text-xs text-muted-foreground">
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap justify-center sm:justify-start">
            <p>© 2026 {BRAND_NAME}</p>
            <Link to="/regulamin" className="hover:text-foreground transition-colors">
              Regulamin
            </Link>
            <Link to="/polityka-prywatnosci" className="hover:text-foreground transition-colors">
              Prywatność
            </Link>
            <button
              onClick={resetConsent}
              className="hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              <Cookie className="w-3.5 h-3.5" />
              Cookies
            </button>
          </div>
          <p className="inline-flex items-center gap-1.5">
            Stworzone z <Heart className="w-3.5 h-3.5 text-destructive fill-destructive" /> dla zwierząt
          </p>
        </div>
      </div>
    </footer>
  );
};
